import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, FilterIcon, StarIcon, MapPinIcon, CalendarIcon, MessageSquareIcon, XIcon, CheckIcon, GraduationCapIcon, ClockIcon, BookmarkIcon, ChevronRightIcon, BriefcaseIcon, UserIcon, BadgeIcon, ThumbsUpIcon, ThumbsDownIcon, UsersIcon, ChevronDownIcon, BellIcon, TargetIcon } from 'lucide-react';
interface Mentee {
  id: number;
  name: string;
  image: string;
  title: string;
  location: string;
  interests: string[];
  experience: string;
  goals: string[];
  bio: string;
  match: number;
  status: 'pending' | 'approved' | 'rejected';
  education: string;
  languages: string[];
  availability: string;
  desiredMentorship: string[];
  applicationNote: string;
  schedule: {
    day: string;
    slots: string[];
  }[];
}
interface CurrentMentee {
  id: number;
  name: string;
  image: string;
  startDate: string;
  progress: number;
  lastMessage: string;
  tags: string[];
  goals: Array<{
    title: string;
    progress: number;
    trend: string;
  }>;
  lastSession: string;
  nextSession: string;
}
interface MentorMatchingProps {
  userType: 'mentor' | 'mentee' | null;
}
const MentorMatching: React.FC<MentorMatchingProps> = ({
  userType
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMentee, setSelectedMentee] = useState<Mentee | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseType, setResponseType] = useState<'approve' | 'reject' | null>(null);
  const [showRequests, setShowRequests] = useState(true);
  const [activeView, setActiveView] = useState<'mentees' | 'requests'>('mentees');
  const [expandedMenteeCards, setExpandedMenteeCards] = useState<number[]>([]);
  const [selectedCurrentMentee, setSelectedCurrentMentee] = useState<CurrentMentee | null>(null);
  const [showMatchTooltip, setShowMatchTooltip] = useState<number | null>(null);
  const navigate = useNavigate();
  // Add currentMentees data for active mentees
  const currentMentees = [{
    id: 1,
    name: 'Alex Kim',
    image: 'https://randomuser.me/api/portraits/nonbinary/12.jpg',
    startDate: 'Jan 2024',
    progress: 45,
    lastMessage: '2 days ago',
    tags: ['Mental Health', 'Anxiety Management', 'Self-Care'],
    goals: [{
      title: 'Develop Coping Strategies',
      progress: 45,
      trend: '+8%'
    }],
    lastSession: '1 week ago',
    nextSession: 'Tomorrow at 2:00 PM'
  }, {
    id: 2,
    name: 'Sam Rivera',
    image: 'https://randomuser.me/api/portraits/nonbinary/15.jpg',
    startDate: 'Dec 2023',
    progress: 75,
    lastMessage: '1 day ago',
    tags: ['Identity', 'Coming Out', 'Family Relations'],
    goals: [{
      title: 'Navigate Family Conversations',
      progress: 80,
      trend: '+15%'
    }],
    lastSession: '2 days ago',
    nextSession: 'Friday at 11:00 AM'
  }, {
    id: 3,
    name: 'Jamie Lee',
    image: 'https://randomuser.me/api/portraits/women/33.jpg',
    startDate: 'Feb 2024',
    progress: 25,
    lastMessage: '3 days ago',
    tags: ['Career Growth', 'Workplace Navigation', 'Leadership'],
    goals: [{
      title: 'Promotion Preparation',
      progress: 30,
      trend: '+5%'
    }],
    lastSession: '1 week ago',
    nextSession: 'Monday at 4:00 PM'
  }, {
    id: 4,
    name: 'Chris Morgan',
    image: 'https://randomuser.me/api/portraits/nonbinary/22.jpg',
    startDate: 'Nov 2023',
    progress: 90,
    lastMessage: '1 day ago',
    tags: ['Relationships', 'Communication', 'Dating'],
    goals: [{
      title: 'Build Healthy Relationships',
      progress: 90,
      trend: '+12%'
    }],
    lastSession: '3 days ago',
    nextSession: 'Thursday at 3:00 PM'
  }];
  // Add mentees data for requests view
  const mentees: Mentee[] = [{
    id: 1,
    name: 'Taylor Chen',
    image: 'https://randomuser.me/api/portraits/women/33.jpg',
    title: 'Graduate Student',
    location: 'Chicago, IL',
    interests: ['Mental Health', 'Anxiety Management', 'Work-Life Balance'],
    experience: '1st year grad student',
    goals: ['Develop better stress management techniques', 'Build work-life balance strategies', 'Create sustainable self-care routines'],
    bio: 'First-year graduate student seeking guidance on managing academic pressure and maintaining mental well-being while navigating personal identity.',
    match: 92,
    status: 'pending',
    education: 'BS in Psychology',
    languages: ['English', 'Mandarin'],
    availability: '8 hours/month',
    desiredMentorship: ['Mental Health', 'Self-Care', 'Identity'],
    applicationNote: "I'm looking for support in managing anxiety and building resilience while pursuing my academic goals. As a queer student in academia, I want to learn how to balance my studies with self-care and personal growth.",
    schedule: [{
      day: 'Monday',
      slots: ['10:00 AM', '2:00 PM']
    }, {
      day: 'Wednesday',
      slots: ['3:00 PM', '5:00 PM']
    }]
  }, {
    id: 2,
    name: 'Jordan Patel',
    image: 'https://randomuser.me/api/portraits/men/32.jpg',
    title: 'Marketing Coordinator',
    location: 'New York, NY',
    interests: ['Identity', 'Career Development', 'Coming Out'],
    experience: '3 years',
    goals: ['Navigate coming out at work', 'Advance career while being authentic', 'Build professional network'],
    bio: 'Marketing professional seeking guidance on coming out in the workplace while advancing my career. Looking for mentorship in building an authentic professional presence.',
    match: 85,
    status: 'pending',
    education: 'BA in Communications',
    languages: ['English', 'Hindi'],
    availability: '6 hours/month',
    desiredMentorship: ['Career Growth', 'Identity', 'Leadership'],
    applicationNote: "I'm at a crucial point in my career and personal journey, preparing to come out at work while pursuing a promotion. I'm seeking guidance on navigating this intersection of professional growth and personal authenticity.",
    schedule: [{
      day: 'Tuesday',
      slots: ['9:00 AM', '1:00 PM']
    }, {
      day: 'Thursday',
      slots: ['2:00 PM', '4:00 PM']
    }]
  }];
  const filterMentees = () => {
    return mentees.filter(mentee => {
      const matchesSearch = mentee.name.toLowerCase().includes(searchQuery.toLowerCase()) || mentee.title.toLowerCase().includes(searchQuery.toLowerCase()) || mentee.interests.some(int => int.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategories = selectedCategories.length === 0 || selectedCategories.some(cat => mentee.desiredMentorship.includes(cat));
      const matchesAvailability = selectedAvailability.length === 0 || selectedAvailability.includes(mentee.availability);
      return matchesSearch && matchesCategories && matchesAvailability;
    });
  };
  const FilterModal = () => <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1500">
      <div className="bg-white rounded-xl w-full max-w-md" data-id="element-1501">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1502">
          <h3 className="font-semibold" data-id="element-1503">Filter Requests</h3>
          <button onClick={() => setShowFilters(false)} data-id="element-1504">
            <XIcon size={20} data-id="element-1505" />
          </button>
        </div>
        <div className="p-4" data-id="element-1506">
          <div className="mb-4" data-id="element-1507">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1508">
              Mentorship Categories
            </label>
            <div className="grid grid-cols-2 gap-2" data-id="element-1509">
              {['Career Growth', 'Leadership', 'Technical Skills', 'Work-Life Balance', 'Portfolio Development', 'Strategic Thinking'].map(category => <label key={category} className="flex items-center" data-id="element-1510">
                  <input type="checkbox" checked={selectedCategories.includes(category)} onChange={() => {
                setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
              }} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-1511" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-1512">{category}</span>
                </label>)}
            </div>
          </div>
          <div className="mb-4" data-id="element-1513">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1514">
              Availability
            </label>
            <div className="space-y-2" data-id="element-1515">
              {['4 hours/month', '8 hours/month', '10 hours/month', '12+ hours/month'].map(availability => <label key={availability} className="flex items-center" data-id="element-1516">
                  <input type="checkbox" checked={selectedAvailability.includes(availability)} onChange={() => {
                setSelectedAvailability(prev => prev.includes(availability) ? prev.filter(a => a !== availability) : [...prev, availability]);
              }} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-1517" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-1518">
                    {availability}
                  </span>
                </label>)}
            </div>
          </div>
          <div className="flex justify-end space-x-3" data-id="element-1519">
            <button onClick={() => {
            setSelectedCategories([]);
            setSelectedAvailability([]);
          }} className="px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg" data-id="element-1520">
              Reset
            </button>
            <button onClick={() => setShowFilters(false)} className="px-4 py-2 bg-purple-600 text-white rounded-lg" data-id="element-1521">
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>;
  const MenteeCard = ({
    mentee
  }: {
    mentee: Mentee;
  }) => <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-1522">
      <div className="p-4" data-id="element-1523">
        <div className="flex items-start justify-between mb-3" data-id="element-1524">
          <div className="flex items-center" data-id="element-1525">
            <img src={mentee.image} alt={mentee.name} className="w-12 h-12 rounded-full object-cover" data-id="element-1526" />
            <div className="ml-3" data-id="element-1527">
              <h3 className="font-semibold" data-id="element-1528">{mentee.name}</h3>
              <p className="text-sm text-gray-600" data-id="element-1529">{mentee.title}</p>
            </div>
          </div>
          <div className="flex flex-col items-end" data-id="element-1530">
            <button onClick={e => {
            e.stopPropagation();
            setShowMatchTooltip(mentee.id);
          }} className="flex items-center bg-purple-50 px-2 py-1 rounded-full hover:bg-purple-100 transition-colors" data-id="element-1531">
              <StarIcon size={14} className="text-purple-600 mr-1" data-id="element-1532" />
              <span className="text-sm font-medium text-purple-600" data-id="element-1533">
                {mentee.match}% Match
              </span>
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 mb-3" data-id="element-1534">
          {mentee.interests.slice(0, 2).map(interest => <span key={interest} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full" data-id="element-1535">
              {interest}
            </span>)}
          {mentee.interests.length > 2 && <span className="text-xs text-gray-500" data-id="element-1536">
              +{mentee.interests.length - 2} more
            </span>}
        </div>
        <div className="text-sm text-gray-600 mb-3 line-clamp-2 cursor-pointer hover:text-gray-900" onClick={() => setSelectedMentee(mentee)} data-id="element-1537">
          {mentee.bio}
        </div>
        <div className="flex justify-between items-center" data-id="element-1538">
          <div className="text-sm text-gray-500" data-id="element-1539">
            <MapPinIcon size={14} className="inline mr-1" data-id="element-1540" />
            {mentee.location}
          </div>
          <button onClick={() => setSelectedMentee(mentee)} className="text-sm bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700" data-id="element-1541">
            Review Request
          </button>
        </div>
      </div>
    </div>;
  const CurrentMenteeCard = ({
    mentee
  }: {
    mentee: CurrentMentee;
  }) => {
    const isExpanded = expandedMenteeCards.includes(mentee.id);
    const requiresAttention = needsAttention(mentee.lastMessage);
    const isHighProgress = mentee.progress >= 75;
    const isLowProgress = mentee.progress < 40;
    // Add defensive check for tags
    const menteeHasTags = Array.isArray(mentee.tags) && mentee.tags.length > 0;
    // Progress text color
    let progressTextColor = 'text-purple-600';
    if (isHighProgress) {
      progressTextColor = 'text-green-600';
    } else if (isLowProgress) {
      progressTextColor = 'text-red-500';
    }
    // New message indicator
    const hasRecentActivity = mentee.lastMessage === '1 day ago';
    const handleProfileClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card expansion
      setSelectedCurrentMentee(mentee); // Use the separate state variable
    };
    return <div className={`w-full bg-white rounded-xl shadow-sm overflow-hidden ${requiresAttention ? 'ring-1 ring-amber-200' : ''}`} data-id="element-1542">
        <div className="flex-1 p-4" data-id="element-1543">
          {/* 1. Header Section */}
          <div className="flex items-center justify-between" data-id="element-1544">
            <div className="flex items-center" data-id="element-1545">
              {/* Avatar without progress ring */}
              <div className="cursor-pointer" onClick={handleProfileClick} data-id="element-1546">
                <img src={mentee.image} alt={mentee.name} className="w-12 h-12 rounded-full object-cover border-2 border-white hover:opacity-90 transition-opacity" data-id="element-1547" />
              </div>
              {/* Name and duration */}
              <div className="ml-3" data-id="element-1548">
                <h3 className="font-semibold text-gray-900" data-id="element-1549">{mentee.name}</h3>
                <p className="text-xs text-gray-500" data-id="element-1550">
                  Since {mentee.startDate}
                </p>
              </div>
            </div>
            {/* Status badge */}
            <div className="flex items-center" data-id="element-1551">
              {isHighProgress && <div className="flex items-center px-2 py-0.5 bg-green-50 rounded-full" data-id="element-1552">
                  <CheckIcon size={12} className="text-green-600 mr-1" data-id="element-1553" />
                  <span className="text-xs text-green-600 font-medium" data-id="element-1554">
                    Active
                  </span>
                </div>}
              {requiresAttention && <div className="flex items-center px-2 py-0.5 bg-amber-50 rounded-full" data-id="element-1555">
                  <BellIcon size={12} className="text-amber-600 mr-1" data-id="element-1556" />
                  <span className="text-xs text-amber-600 font-medium" data-id="element-1557">
                    Nudge Needed
                  </span>
                </div>}
            </div>
          </div>
          {/* 2. Tag Section */}
          <div className="flex flex-wrap gap-1.5 mt-3" data-id="element-1558">
            {menteeHasTags && mentee.tags.slice(0, 2).map((tag: string) => <span key={tag} className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full" data-id="element-1559">
                  {tag}
                </span>)}
            {menteeHasTags && mentee.tags.length > 2 && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full cursor-pointer" onClick={() => toggleCardExpansion(mentee.id)} data-id="element-1560">
                +{mentee.tags.length - 2} more
              </span>}
          </div>
          {/* 3. Progress Section */}
          <div className="mt-4" data-id="element-1561">
            <div className="flex items-center mb-1" data-id="element-1562">
              <TargetIcon size={14} className="text-purple-600 mr-1.5" data-id="element-1563" />
              <span className="text-xs text-gray-700" data-id="element-1564">
                Top Goal: {menteeHasTags ? mentee.tags[0] : 'No goals set'}
              </span>
            </div>
            <div className="flex items-center mb-1" data-id="element-1565">
              <div className="flex-1 mr-2" data-id="element-1566">
                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden" data-id="element-1567">
                  <div className="h-2 rounded-full progress-animation" style={{
                  width: `${mentee.progress}%`,
                  background: 'linear-gradient(90deg, #8b5cf6 0%, #a78bfa 100%)'
                }} data-id="element-1568" />
                </div>
              </div>
              <span className={`text-xs font-medium ${progressTextColor}`} data-id="element-1569">
                {mentee.progress}%
              </span>
            </div>
          </div>
          {/* 4. Activity Overview */}
          <div className="mt-3 space-y-1" data-id="element-1570">
            <div className="flex items-center" data-id="element-1571">
              <CalendarIcon size={14} className="text-gray-400 mr-1.5" data-id="element-1572" />
              <span className="text-xs text-gray-600" data-id="element-1573">
                Last session: {mentee.lastSession}
              </span>
              {hasRecentActivity && <span className="ml-2 text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded" data-id="element-1574">
                  New
                </span>}
            </div>
            <div className="flex items-center" data-id="element-1575">
              <MessageSquareIcon size={14} className="text-gray-400 mr-1.5" data-id="element-1576" />
              <span className="text-xs text-gray-600" data-id="element-1577">
                Last message: {mentee.lastMessage}
              </span>
            </div>
          </div>
          {/* 5. Action Buttons */}
          <div className="mt-4 space-y-2" data-id="element-1578">
            <button onClick={e => {
            e.stopPropagation();
            // View goals action
          }} className="w-full py-2 text-center text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50" data-id="element-1579">
              View Goals
            </button>
            <button onClick={e => {
            e.stopPropagation();
            // Add note action
          }} className="w-full py-2 text-center text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50" data-id="element-1580">
              Add Note
            </button>
            {requiresAttention && <button onClick={e => {
            e.stopPropagation();
            // Send nudge action
          }} className="w-full py-2 text-center text-sm bg-amber-50 border border-amber-200 rounded-lg text-amber-600 hover:bg-amber-100" data-id="element-1581">
                Send Nudge
              </button>}
            <button onClick={e => {
            e.stopPropagation();
            navigate('/messages');
          }} className="w-full py-2 text-center text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors" data-id="element-1582">
              Message
            </button>
          </div>
          {/* Expanded content for tags */}
          {isExpanded && <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1583">
              <h4 className="text-xs font-medium text-gray-700 mb-2" data-id="element-1584">
                All Tags
              </h4>
              <div className="flex flex-wrap gap-1.5" data-id="element-1585">
                {mentee.tags.map((tag: string) => <span key={tag} className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full" data-id="element-1586">
                    {tag}
                  </span>)}
              </div>
            </div>}
        </div>
      </div>;
  };
  const CurrentMenteeProfileModal = ({
    mentee
  }: {
    mentee: CurrentMentee;
  }) => {
    if (!mentee) return null;
    // Add the defensive check here, at the top level of the modal component
    const menteeHasTags = Array.isArray(mentee.tags) && mentee.tags.length > 0;
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1587">
        <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-id="element-1588">
          <div className="p-6" data-id="element-1589">
            {/* Header with photo and basic info */}
            <div className="flex justify-between items-start mb-6" data-id="element-1590">
              <div className="flex items-start" data-id="element-1591">
                <img src={mentee.image} alt={mentee.name} className="w-16 h-16 rounded-full object-cover" data-id="element-1592" />
                <div className="ml-4" data-id="element-1593">
                  <h2 className="text-xl font-semibold" data-id="element-1594">{mentee.name}</h2>
                  <p className="text-gray-600 mb-2" data-id="element-1595">Since {mentee.startDate}</p>
                  <div className="flex items-center gap-3" data-id="element-1596">
                    <div className="flex items-center text-sm text-gray-500 whitespace-nowrap" data-id="element-1597">
                      <CalendarIcon size={14} className="mr-1 flex-shrink-0" data-id="element-1598" />
                      <span data-id="element-1599">Last session: {mentee.lastSession}</span>
                    </div>
                  </div>
                </div>
              </div>
              <button onClick={() => setSelectedCurrentMentee(null)} className="ml-4 text-gray-400 hover:text-gray-500" data-id="element-1600">
                <XIcon size={20} data-id="element-1601" />
              </button>
            </div>
            {/* Progress section */}
            <div className="mb-6" data-id="element-1602">
              <h3 className="font-medium text-gray-900 mb-2" data-id="element-1603">Progress</h3>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2" data-id="element-1604">
                <div className="bg-purple-600 h-2 rounded-full transition-all duration-300" style={{
                width: `${mentee.progress}%`
              }} data-id="element-1605" />
              </div>
              <p className="text-sm text-gray-600" data-id="element-1606">
                Overall progress: {mentee.progress}%
              </p>
            </div>
            {/* Tags/Skills section - now properly guarded */}
            {menteeHasTags && <div className="mb-6" data-id="element-1607">
                <h3 className="font-medium text-gray-900 mb-3" data-id="element-1608">Focus Areas</h3>
                <div className="flex flex-wrap gap-2" data-id="element-1609">
                  {mentee.tags.map(tag => <span key={tag} className="text-sm bg-purple-50 text-purple-600 px-3 py-1 rounded-full" data-id="element-1610">
                      {tag}
                    </span>)}
                </div>
              </div>}
            {/* Goals section */}
            {mentee.goals && mentee.goals.length > 0 && <div className="mb-6" data-id="element-1611">
                <h3 className="font-medium text-gray-900 mb-3" data-id="element-1612">
                  Current Goals
                </h3>
                <div className="space-y-3" data-id="element-1613">
                  {mentee.goals.map(goal => <div key={goal.title} className="bg-gray-50 p-3 rounded-lg" data-id="element-1614">
                      <div className="flex justify-between items-center mb-1" data-id="element-1615">
                        <span className="font-medium" data-id="element-1616">{goal.title}</span>
                        <span className="text-sm text-green-600" data-id="element-1617">
                          {goal.trend}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-1" data-id="element-1618">
                        <div className="bg-purple-600 h-2 rounded-full" style={{
                    width: `${goal.progress}%`
                  }} data-id="element-1619" />
                      </div>
                      <div className="text-right text-xs text-gray-500" data-id="element-1620">
                        {goal.progress}% complete
                      </div>
                    </div>)}
                </div>
              </div>}
            {/* Action buttons */}
            <div className="flex flex-col gap-3" data-id="element-1621">
              <button onClick={() => navigate('/messages')} className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center font-medium" data-id="element-1622">
                <MessageSquareIcon size={16} className="mr-2" data-id="element-1623" />
                Message {mentee.name}
              </button>
              <div className="grid grid-cols-2 gap-3" data-id="element-1624">
                <button className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center justify-center" onClick={() => setSelectedCurrentMentee(null)} data-id="element-1625">
                  <CalendarIcon size={16} className="mr-2" data-id="element-1626" />
                  Schedule Session
                </button>
                <button className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center justify-center" onClick={() => setSelectedCurrentMentee(null)} data-id="element-1627">
                  <BookmarkIcon size={16} className="mr-2" data-id="element-1628" />
                  View Full Details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  const MenteeProfileModal = ({
    mentee
  }: {
    mentee: Mentee;
  }) => {
    const [isNoteExpanded, setIsNoteExpanded] = useState(false);
    const noteRef = useRef<HTMLParagraphElement>(null);
    const [isNoteLong, setIsNoteLong] = useState(false);
    useEffect(() => {
      if (noteRef.current) {
        const lineHeight = parseInt(window.getComputedStyle(noteRef.current).lineHeight);
        const height = noteRef.current.scrollHeight;
        setIsNoteLong(height > lineHeight * 6); // More than 6 lines
      }
    }, [mentee.applicationNote]);
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1629">
        <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-id="element-1630">
          <div className="p-6" data-id="element-1631">
            {/* Header with photo and basic info */}
            <div className="flex justify-between items-start mb-6" data-id="element-1632">
              <div className="flex items-start" data-id="element-1633">
                <img src={mentee.image} alt={mentee.name} className="w-16 h-16 rounded-full object-cover" data-id="element-1634" />
                <div className="ml-4" data-id="element-1635">
                  <h2 className="text-xl font-semibold" data-id="element-1636">{mentee.name}</h2>
                  <p className="text-gray-600 mb-2" data-id="element-1637">{mentee.title}</p>
                  <div className="flex items-center gap-3" data-id="element-1638">
                    <div className="flex items-center text-sm text-gray-500 whitespace-nowrap" data-id="element-1639">
                      <MapPinIcon size={14} className="mr-1 flex-shrink-0" data-id="element-1640" />
                      <span data-id="element-1641">{mentee.location}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500 whitespace-nowrap" data-id="element-1642">
                      <ClockIcon size={14} className="mr-1 flex-shrink-0" data-id="element-1643" />
                      <span data-id="element-1644">{mentee.availability}</span>
                    </div>
                  </div>
                </div>
              </div>
              <button onClick={() => setSelectedMentee(null)} className="ml-4 text-gray-400 hover:text-gray-500" data-id="element-1645">
                <XIcon size={20} data-id="element-1646" />
              </button>
            </div>
            {/* Match score badge */}
            <div className="mb-3" data-id="element-1647">
              <button onClick={() => setShowMatchTooltip(mentee.id)} className="inline-flex items-center bg-purple-50 px-3 py-1 rounded-full hover:bg-purple-100 transition-colors" data-id="element-1648">
                <StarIcon size={14} className="text-purple-600 mr-1.5" data-id="element-1649" />
                <span className="text-sm font-medium text-purple-600" data-id="element-1650">
                  {mentee.match}% Match
                </span>
              </button>
            </div>
            {/* About section */}
            <div className="mb-6" data-id="element-1651">
              <h3 className="font-medium text-gray-900 mb-2" data-id="element-1652">About</h3>
              <p className="text-gray-600" data-id="element-1653">{mentee.bio}</p>
            </div>
            <hr className="my-6 border-gray-200" data-id="element-1654" />
            {/* Goals and mentorship areas */}
            <div className="grid grid-cols-2 gap-6 mb-6" data-id="element-1655">
              <div data-id="element-1656">
                <h3 className="font-medium text-gray-900 mb-3" data-id="element-1657">Goals</h3>
                <div className="space-y-2" data-id="element-1658">
                  {mentee.goals.map(goal => <div key={goal} className="flex items-start group" data-id="element-1659">
                      <CheckIcon size={16} className="text-purple-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1660" />
                      <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors" data-id="element-1661">
                        {goal}
                      </span>
                    </div>)}
                </div>
              </div>
              <div data-id="element-1662">
                <h3 className="font-medium text-gray-900 mb-3" data-id="element-1663">
                  Desired Mentorship Areas
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-1664">
                  {mentee.desiredMentorship.slice(0, 3).map(area => <span key={area} className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium" data-id="element-1665">
                      {area}
                    </span>)}
                  {mentee.desiredMentorship.length > 3 && <button className="text-sm text-purple-600 hover:text-purple-700 font-medium" data-id="element-1666">
                      +{mentee.desiredMentorship.length - 3} more
                    </button>}
                </div>
              </div>
            </div>
            <hr className="my-6 border-gray-200" data-id="element-1667" />
            {/* Background section */}
            <div className="mb-6" data-id="element-1668">
              <h3 className="font-medium text-gray-900 mb-3" data-id="element-1669">Background</h3>
              <div className="space-y-2" data-id="element-1670">
                <div className="flex items-center text-gray-600" data-id="element-1671">
                  <GraduationCapIcon size={16} className="mr-2 text-gray-400" data-id="element-1672" />
                  <span data-id="element-1673">{mentee.education}</span>
                </div>
                <div className="flex items-center text-gray-600" data-id="element-1674">
                  <BriefcaseIcon size={16} className="mr-2 text-gray-400" data-id="element-1675" />
                  <span data-id="element-1676">{mentee.experience} of experience</span>
                </div>
              </div>
            </div>
            <hr className="my-6 border-gray-200" data-id="element-1677" />
            {/* Application note with expand/collapse */}
            <div className="mb-6" data-id="element-1678">
              <h3 className="font-medium text-gray-900 mb-3" data-id="element-1679">
                Application Note
              </h3>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1680">
                <div className="pl-10 relative" data-id="element-1681">
                  <div className="absolute top-0 left-0 text-purple-200" data-id="element-1682">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" data-id="element-1683">
                      <path d="M15.2 4H18.8C20.56 4 22 5.44 22 7.2V10.8C22 12.56 20.56 14 18.8 14H15.2C13.44 14 12 12.56 12 10.8V7.2C12 5.44 13.44 4 15.2 4ZM5.2 4H8.8C10.56 4 12 5.44 12 7.2V10.8C12 12.56 10.56 14 8.8 14H5.2C3.44 14 2 12.56 2 10.8V7.2C2 5.44 3.44 4 5.2 4ZM15.2 14H18.8C20.56 14 22 15.44 22 17.2V20.8C22 22.56 20.56 24 18.8 24H15.2C13.44 24 12 22.56 12 20.8V17.2C12 15.44 13.44 14 15.2 14ZM5.2 14H8.8C10.56 14 12 15.44 12 17.2V20.8C12 22.56 10.56 24 8.8 24H5.2C3.44 24 2 22.56 2 20.8V17.2C2 15.44 3.44 14 5.2 14Z" fill="currentColor" data-id="element-1684" />
                    </svg>
                  </div>
                  <div data-id="element-1685">
                    <p ref={noteRef} className={`text-gray-600 text-sm whitespace-pre-wrap ${!isNoteExpanded && isNoteLong ? 'line-clamp-6' : ''}`} data-id="element-1686">
                      {mentee.applicationNote}
                    </p>
                    {isNoteLong && <button onClick={() => setIsNoteExpanded(!isNoteExpanded)} className="text-purple-600 text-sm font-medium mt-2 hover:text-purple-700" data-id="element-1687">
                        {isNoteExpanded ? 'Show Less' : 'Read More'}
                      </button>}
                  </div>
                </div>
              </div>
            </div>
            {/* Action buttons - vertical stack */}
            <div className="flex flex-col gap-3" data-id="element-1688">
              <button onClick={() => {
              setResponseType('approve');
              setShowResponseModal(true);
            }} className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center font-medium" data-id="element-1689">
                <ThumbsUpIcon size={16} className="mr-2" data-id="element-1690" />
                Accept Request
              </button>
              <button onClick={() => {
              setResponseType('reject');
              setShowResponseModal(true);
            }} className="w-full px-4 py-3 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center justify-center font-medium" data-id="element-1691">
                <ThumbsDownIcon size={16} className="mr-2" data-id="element-1692" />
                Decline Request
              </button>
              <div className="grid grid-cols-2 gap-3" data-id="element-1693">
                <button className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center justify-center" data-id="element-1694">
                  <BookmarkIcon size={16} className="mr-2" data-id="element-1695" />
                  Save Profile
                </button>
                <button className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center justify-center" data-id="element-1696">
                  <MessageSquareIcon size={16} className="mr-2" data-id="element-1697" />
                  Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  const ScheduleModal = () => <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1698">
      <div className="bg-white rounded-xl w-full max-w-md" data-id="element-1699">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1700">
          <h3 className="font-semibold" data-id="element-1701">Preferred Meeting Times</h3>
          <button onClick={() => setShowScheduleModal(false)} data-id="element-1702">
            <XIcon size={20} data-id="element-1703" />
          </button>
        </div>
        <div className="p-4" data-id="element-1704">
          {selectedMentee?.schedule.map(day => <div key={day.day} className="mb-4 last:mb-0" data-id="element-1705">
              <h4 className="font-medium text-sm mb-2" data-id="element-1706">{day.day}</h4>
              <div className="grid grid-cols-3 gap-2" data-id="element-1707">
                {day.slots.map(slot => <button key={slot} className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:border-purple-500 hover:text-purple-600" data-id="element-1708">
                    {slot}
                  </button>)}
              </div>
            </div>)}
          <div className="mt-6 flex justify-end" data-id="element-1709">
            <button onClick={() => setShowScheduleModal(false)} className="px-4 py-2 bg-purple-600 text-white rounded-lg" data-id="element-1710">
              Confirm Availability
            </button>
          </div>
        </div>
      </div>
    </div>;
  const ResponseModal = () => <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1711">
      <div className="bg-white rounded-xl w-full max-w-md" data-id="element-1712">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1713">
          <h3 className="font-semibold" data-id="element-1714">
            {responseType === 'approve' ? 'Accept Request' : 'Decline Request'}
          </h3>
          <button onClick={() => setShowResponseModal(false)} data-id="element-1715">
            <XIcon size={20} data-id="element-1716" />
          </button>
        </div>
        <div className="p-4" data-id="element-1717">
          <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4" rows={4} placeholder={responseType === 'approve' ? 'Add a personal message to welcome your new mentee...' : "Add a message explaining why you're declining (optional)..."} data-id="element-1718" />
          <div className="flex justify-end space-x-3" data-id="element-1719">
            <button onClick={() => setShowResponseModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700" data-id="element-1720">
              Cancel
            </button>
            <button onClick={() => {
            setShowResponseModal(false);
            setSelectedMentee(null);
          }} className={`px-4 py-2 rounded-lg text-white ${responseType === 'approve' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'}`} data-id="element-1721">
              {responseType === 'approve' ? 'Send Acceptance' : 'Send Decline'}
            </button>
          </div>
        </div>
      </div>
    </div>;
  const toggleCardExpansion = (id: number) => {
    setExpandedMenteeCards(prev => prev.includes(id) ? prev.filter(cardId => cardId !== id) : [...prev, id]);
  };
  const needsAttention = (lastMessage: string) => {
    // Parse days from "X days ago" format
    const days = parseInt(lastMessage.split(' ')[0]);
    return days >= 7;
  };
  const MatchScoreTooltip = ({
    score,
    isVisible,
    onClose
  }: {
    score: number;
    isVisible: boolean;
    onClose: () => void;
  }) => {
    if (!isVisible) return null;
    return <div className="fixed inset-0 flex items-center justify-center z-50" onClick={onClose} data-id="element-1722">
        <div className="absolute inset-0 bg-black opacity-50" data-id="element-1723"></div>
        <div className="bg-white rounded-xl p-6 max-w-sm relative z-10" onClick={e => e.stopPropagation()} data-id="element-1724">
          <h3 className="text-lg font-semibold mb-4 flex items-center" data-id="element-1725">
            <StarIcon size={20} className="text-purple-600 mr-2" data-id="element-1726" />
            {score}% Match Score
          </h3>
          <p className="text-gray-600 text-sm mb-4" data-id="element-1727">
            Your match score is calculated based on:
          </p>
          <ul className="space-y-3 mb-4" data-id="element-1728">
            <li className="flex items-start text-sm" data-id="element-1729">
              <span className="text-purple-600 mr-2" data-id="element-1730">•</span>
              <span data-id="element-1731">Shared mentorship categories and interests</span>
            </li>
            <li className="flex items-start text-sm" data-id="element-1732">
              <span className="text-purple-600 mr-2" data-id="element-1733">•</span>
              <span data-id="element-1734">Communication style compatibility</span>
            </li>
            <li className="flex items-start text-sm" data-id="element-1735">
              <span className="text-purple-600 mr-2" data-id="element-1736">•</span>
              <span data-id="element-1737">Availability and scheduling alignment</span>
            </li>
            <li className="flex items-start text-sm" data-id="element-1738">
              <span className="text-purple-600 mr-2" data-id="element-1739">•</span>
              <span data-id="element-1740">Experience level relevance</span>
            </li>
          </ul>
          <button onClick={onClose} className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-1741">
            Got it
          </button>
        </div>
      </div>;
  };
  return <div className="pb-4" data-id="element-1742">
      <div className="mb-6" data-id="element-1743">
        <h1 className="text-xl font-bold mb-4" data-id="element-1744">Mentor Hub</h1>
        <div className="bg-gray-100 p-1 rounded-full inline-flex mb-4" data-id="element-1745">
          <button className={`px-6 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${activeView === 'mentees' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} onClick={() => setActiveView('mentees')} data-id="element-1746">
            My Mentees
          </button>
          <button className={`px-6 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${activeView === 'requests' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} onClick={() => setActiveView('requests')} data-id="element-1747">
            Requests
          </button>
        </div>
      </div>
      {activeView === 'mentees' ?
    // Current Mentees Section - updated grid for better responsiveness
    <div className="space-y-6" data-id="element-1748">
          {currentMentees.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1749">
              {currentMentees.map(mentee => <CurrentMenteeCard key={mentee.id} mentee={mentee} data-id="element-1750" />)}
            </div> : <div className="bg-gray-50 rounded-lg p-6 text-center" data-id="element-1751">
              <UsersIcon size={32} className="mx-auto text-gray-400 mb-2" data-id="element-1752" />
              <h3 className="font-medium text-gray-700 mb-1" data-id="element-1753">
                No Current Mentees
              </h3>
              <p className="text-gray-500 text-sm mb-4" data-id="element-1754">
                You don't have any active mentees yet. Review requests to get
                started!
              </p>
              <button onClick={() => setActiveView('requests')} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-1755">
                View Requests
              </button>
            </div>}
        </div> :
    // Mentee Requests Section
    <div className="space-y-6" data-id="element-1756">
          <div className="flex items-center space-x-4 mb-4" data-id="element-1757">
            <div className="flex-1 relative" data-id="element-1758">
              <SearchIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-1759" />
              <input type="text" placeholder="Search by name, role, or interests..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1760" />
            </div>
            <button onClick={() => setShowFilters(true)} className="px-4 py-2 border border-gray-300 rounded-lg flex items-center hover:bg-gray-50" data-id="element-1761">
              <FilterIcon size={18} className="mr-2" data-id="element-1762" />
              Filters
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1763">
            {filterMentees().map(mentee => <MenteeCard key={mentee.id} mentee={mentee} data-id="element-1764" />)}
          </div>
          {filterMentees().length === 0 && <div className="text-center py-8 text-gray-500" data-id="element-1765">
              No requests found matching your criteria
            </div>}
        </div>}
      {/* Modals */}
      {showFilters && <FilterModal data-id="element-1766" />}
      {selectedMentee && <MenteeProfileModal mentee={selectedMentee} data-id="element-1767" />}
      {selectedCurrentMentee && <CurrentMenteeProfileModal mentee={selectedCurrentMentee} data-id="element-1768" />}
      {showScheduleModal && selectedMentee && <ScheduleModal data-id="element-1769" />}
      {showResponseModal && <ResponseModal data-id="element-1770" />}
      {showMatchTooltip !== null && <MatchScoreTooltip score={mentees.find(m => m.id === showMatchTooltip)?.match || 0} isVisible={showMatchTooltip !== null} onClose={() => setShowMatchTooltip(null)} data-id="element-1771" />}
    </div>;
};
export default MentorMatching;